/*

  b r o w s e r
  Browser

  :description:
  A singleton class that represents pieces/sections of the browser the app is running in.

*/

//
//  :code:
import { talk } from "../js/talk";

//
//  :statics:
//  Variables and static declarations.
export const EYEKANDY_BROWSER_INTERFACE_WINDOW_KEY = "EyekandyBrowserInterface"

//
//  :classes:
export class EyekandyBrowserInterface {
  constructor(options) {
    if (typeof options === 'undefined') {
      options = {}
    }
    //
    //  :core:
    //  Core settings and values.
    this.name = "eyekandy-browser-interface"
    this.version = "v3.0.0"
    this.group = "[EYEKANDY-BROWSER-INTERFACE]"

    //
    //  :options:
    //  The options will change depending on the project, add them after intialisation using addHttpGetOption().
    this.options = {};

    //
    //  :runtime:
    //  After reading options we can save them as "config" settings.
    this.config = {};

    //
    //  :defaults:
    //  Set up our defaults.
    this._setDefaultHTTPGetOptions()

    //
    //  :echo:
    //  Write out into the console that the instance was created.
    talk([this.group, `created a new instance @ '${this.version}'`]);
  }

  //
  //  :internal:
  //  Functions internal to our self.
  //  -   =   -   =   -   =   -   =   -   =   -   =   -   =   -   =   -   =   -   =   -   =   -   =   -   =   -   =   -

  _setDefaultHTTPGetOptions() {
    this.addHttpGetOption("environment", "eky-environment", "dev", String)
    this.addHttpGetOption("console", "eky-logging", false, Boolean, true)
    this.addHttpGetOption("distributor", "eky-distributor", null, String)
    this.addHttpGetOption("language", "eky-language", "en-GB", String)
    this.addHttpGetOption("user", "eky-user", null, String)
    this.addHttpGetOption("arid", "eky-arid", null, String)
    this.addHttpGetOption("session", "eky-session", null, String)
    this.addHttpGetOption("forceEnableAR", "eky-ar-override", false, Boolean, true)
    //
    //  Page is a base64 encoded string.
    this.addHttpGetOption("page", "eky-page", null, (val) => {
      let value = null
      try {
        value = atob(val).toString()
      } catch (err) { return false }
      return value
    })
  }

  _wipe() { this.options = {}; this.config = {} }

  _readConfigValue(key) { return this.config[key] }

  _setConfigValue(key, value) { this.config[key] = value }

  //
  //  :api:
  //  Our main exposed interface for callers.
  //  -   =   -   =   -   =   -   =   -   =   -   =   -   =   -   =   -   =   -   =   -   =   -   =   -   =   -   =   -

  loadJSON(url) {
    return new Promise((resolve) => {
      var xhr = new XMLHttpRequest();
      xhr.addEventListener("error", function (err) {
        if (!err) { err = {} }
        talk(["[EYEKANDY-BROWSER-INTERFACE]", `error downloading JSON`, err.message, err.stack]);
        resolve(false)
      })
      xhr.addEventListener("load", function () {
        try {
          talk(["[EYEKANDY-BROWSER-INTERFACE]", `ok, downloaded '${(this.response.length / 1024).toLocaleString()} kB' from '${url}'`]);
          const data = JSON.parse(this.response)
          resolve(data)
        } catch (err) {
          talk(["[EYEKANDY-BROWSER-INTERFACE]", `error downloading JSON`, err.message, err.stack]);
          resolve(null)
        }
      });
      talk(["[EYEKANDY-BROWSER-INTERFACE]", `starting to load JSON from '${url}'...`]);

      xhr.open("GET", url);
      xhr.send();

    });
  }

  /**
   * A function to add support to read an HTTP GET argument of the given name into config[name], and cast the
   * value if casterFunction is set.
   * @param {String} name The name we'll use to reference this @ this.config[name]
   * @param {String} keyInURL The key that is used in the URL
   * @param {Function} casterFunction An optional function that takes in a single value and returns a single value.
   */
  addHttpGetOption(name, keyInURL, defaultValue, casterFunction, toggleable) {
    this.options[name] = {
      type: "http-get",
      name: name,
      key: keyInURL,
      defaultValue: defaultValue,
      casterFunction: casterFunction ? casterFunction : null,
      toggleable: toggleable ? true : false
    };
  }

  /**
   * A function to read our config values using our options from the current HTTP URL.
   * @param {Boolean} overwrite Allow overwriting a value that we already have in this.config.
   */
  readConfigOptionsFromCurrentUrl(overwrite) {
    //
    //  Set our overwrite value to true by default.
    if (typeof overwrite === "undefined") { overwrite = true };
    //
    //  Echo that we are reading arguments.
    talk([this.group, `starting to read any get arguments from the current URL`]);
    //
    //  Read the get arguments from the current URL.
    const getArgs = new URLSearchParams(window.location.search);
    const argsRead = []
    //
    //  For all of the options that have been configured, read their value.
    for (const [attributeName, optionConfig] of Object.entries(this.options)) {
      //
      //  Read the value from the GET arguments.
      const valueInGetArgs = getArgs.get(optionConfig.key);
      //
      //  Ok, so here there are 2 paths: present or missing.
      let valueToSet = null
      if (valueInGetArgs !== null) {
        //
        //  There is a value that we have read here.
        valueToSet = optionConfig.casterFunction ? optionConfig.casterFunction(valueInGetArgs) : valueInGetArgs
      } else {
        //
        //  For missing values, use the default.
        valueToSet = optionConfig.defaultValue
      }
      //
      //  See if the key is set in the URL.
      let isInURL = window.location.href.indexOf(optionConfig.key) > -1 ? true : false
      //
      //  Record if we read a config option.
      if (isInURL) { argsRead.push(attributeName) }
      //
      //  If the value is missing, the key may be present, check for that in the URL, also =""?
      if (optionConfig.toggleable) { valueToSet = isInURL }
      //
      //  Now update the value of the config we have as part of ourself.
      if (overwrite || typeof this._readConfigValue(attributeName) === "undefined") {
        this._setConfigValue(attributeName, valueToSet)
        if (valueInGetArgs === null) {
          talk([this.group, `set a default value for: ` +
            `'this.config.${attributeName}' = '${valueToSet}' (${typeof valueToSet})`])
        } else {
          talk([this.group, `read a value from the HTTP GET arguments: '${optionConfig.key}' -> ` +
            `'this.config.${attributeName}' = '${valueToSet}' (${typeof valueToSet})`])
        }
      }
    }
    talk([this.group, `read values for '${argsRead.length}' HTTP GET arguments`])
    talk([this.group, `current config:`, this.serialiseConfig()])
  }

  sendMessageToWindow(action, data, targetOrigin) {
    //
    //  Validate our inputs.
    if (!targetOrigin) {
      targetOrigin = "*"; // Likely to cause issues with CSP.
    }
    //
    //  Build our payload.
    const payload = { action: action, ...data };
    //
    //  We'll try to dispatch on our parent if it's there (happens when we are an iframe).
    let sentMessage = false;
    if (window) {
      if (window.parent) {
        try {
          window.parent.postMessage(payload, targetOrigin);
          sentMessage = true;
          talk([`${this.group} sent message to window: `, payload]);
        } catch (err) {
          talk([`${this.group} error, unable to send a message to window: `, payload, err], "error", true);
        }
      }
    }
    //
    //  Return true if we did send to the message.
    return sentMessage;
  }

  addScriptNodeToDOM(id, url, onLoad, onError) {
    //
    //  If the node already exists in this DOM, skip.
    if (document.getElementById(id)) { return false }
    //
    //  With the URL we just need to download it by adding it to the <body>.
    const node = document.createElement("script")
    node.setAttribute("async", "")
    node.setAttribute("defer", "")
    node.setAttribute("id", id)
    //
    //  Set up our event listeners, binding our context to them as to wait for their completion.
    node.onload = onLoad
    node.onerror = onError
    //
    //  Set the URL and add the node to the DOM.
    node.src = url
    document.body.appendChild(node)
    talk([
      this.group,
      `added our <script> node to the DOM @ '#${id}' to load the localisations for this app from '${url}'`
    ])
    return true
  }

  serialise() {
    return {
      name: this.name,
      version: this.version,
      group: this.group,
      options: this.options,
      config: this.config,
    }
  }

  serialiseConfig() {
    return this.config
  }
}
